import { inject } from '@angular/core';
import {
  ColumnFieldEntity,
  ColumnSetupField,
  ColumnView,
  ColumnViewLoad,
  ColumnWithTranslation,
  Field,
  Octopus,
  fieldColumnPrefix,
} from '@maximizer/core/shared/domain';
import {
  Observable,
  ReplaySubject,
  catchError,
  concatMap,
  forkJoin,
  map,
  of,
  share,
  switchMap,
} from 'rxjs';
import { ColumnSetupService } from './column-setup.service';
import { MetadataService } from './metadata.service';

export abstract class ColumnViewService {
  private static readonly singleFieldTemplate = /^\{\{[^{}]+\}\}$/;
  protected static readonly defaultColumnWidth = 150;

  private readonly columnSetupService = inject(ColumnSetupService);
  private readonly metadataService = inject(MetadataService);
  private lastSave: Observable<ColumnView | null> = of(null);

  protected abstract readonly entityType: Octopus.EntityType;
  protected abstract readonly knownColumns: ColumnWithTranslation[];
  protected abstract readonly defaultTemplates: string[];
  protected abstract readonly fieldEntities: ColumnFieldEntity[];
  protected readonly fixedColumnIds: string[] = [];

  loadColumnView(): Observable<ColumnViewLoad> {
    return this.columnSetupService.getCurrentUserView(this.entityType).pipe(
      switchMap((columnSetup) =>
        columnSetup
          ? this.removeMissingFields(this.fromColumnSetup(columnSetup))
          : of({ view: this.getDefaultView(), savable: true }),
      ),
      catchError((error: Error) =>
        of({ view: this.getDefaultView(), savable: false, error }),
      ),
    );
  }

  // Saves run one at a time, in order. Saving a default view creates the user's
  // own view, so a save queued behind it updates that view by its key.
  saveColumnView(view: ColumnView): Observable<ColumnView> {
    const save = this.lastSave.pipe(
      concatMap((previous) => {
        const target =
          this.isDefault(view) && previous && !this.isDefault(previous)
            ? { ...view, key: previous.key, rank: previous.rank }
            : view;

        const columnSetup = new Octopus.ColumnSetupMapper(
          ColumnViewService.defaultColumnWidth,
          this.entityType,
        ).from(target);
        return this.columnSetupService.setCurrentUserView(columnSetup).pipe(
          map((saved) => ({
            ...target,
            key: saved?.Key ?? target.key,
            rank: saved?.Rank ?? target.rank,
          })),
        );
      }),
      // Runs once: a failed save is not sent again when the next one waits for it.
      share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    );

    this.lastSave = save.pipe(catchError(() => of(null)));
    return save;
  }

  getDefaultView(): ColumnView {
    return {
      key: null,
      rank: Octopus.ColumnSetupRank.Default,
      columns: this.defaultTemplates.map((template) =>
        this.createColumn(template),
      ),
    };
  }

  createColumn(
    template: string,
    header = '',
    width?: number,
  ): ColumnWithTranslation {
    const known = this.knownColumns.find((item) => item.field === template);
    if (known) {
      return { ...known, size: width ?? known.size };
    }

    return {
      id: `${fieldColumnPrefix}${this.getPath(template)}`,
      field: template,
      title: { translationID: '', resourceID: '', defaultText: header },
      locked: false,
      sticky: false,
      hidden: false,
      groupable: false,
      size: width ?? ColumnViewService.defaultColumnWidth,
    };
  }

  getFieldKey(item: ColumnWithTranslation): string {
    const path = this.getPath(item.field);
    return this.getRelatedEntityIndex(`/${path}`) > 0
      ? `/${path}`
      : `${this.ownKeyPrefix}${path}`;
  }

  isDefault(view: ColumnView): boolean {
    return !view.key || view.rank === Octopus.ColumnSetupRank.Default;
  }

  isRemovable(item: ColumnWithTranslation): boolean {
    return !this.fixedColumnIds.includes(item.id);
  }

  fromSetupFields(
    fields: ColumnSetupField[],
    current: ColumnWithTranslation[],
  ): ColumnWithTranslation[] {
    return fields.map((field) => {
      const existing = current.find(
        (item) => this.getFieldKey(item) === field.id,
      );
      return (
        existing ?? this.createColumn(this.getTemplate(field.id), field.name)
      );
    });
  }

  private get ownKeyPrefix(): string {
    return `/${this.fieldEntities[0].entity}/`;
  }

  private getRelatedEntityIndex(fieldKey: string): number {
    const index = this.fieldEntities.findIndex(
      (item, position) =>
        position > 0 && fieldKey.startsWith(`/${item.entity}/`),
    );
    return Math.max(index, 0);
  }

  private getTemplate(fieldKey: string): string {
    const path = fieldKey.startsWith(this.ownKeyPrefix)
      ? fieldKey.substring(this.ownKeyPrefix.length)
      : fieldKey.substring(1);
    return `{{${path}}}`;
  }

  private getPath(template: string): string {
    return template.replace(/^\{\{/, '').replace(/\}\}$/, '');
  }

  private removeMissingFields(view: ColumnView): Observable<ColumnViewLoad> {
    const added = view.columns.filter(
      (column) => !this.knownColumns.some((known) => known.id === column.id),
    );
    if (!added.length) {
      return of({ view, savable: true });
    }

    return forkJoin(
      this.fieldEntities.map((item) =>
        this.getFieldKeys(item.entity, item.compatibility),
      ),
    ).pipe(
      map((fieldKeys) => {
        const removedColumns = added.filter(
          (column) => !this.hasField(column, fieldKeys),
        );
        const columns = view.columns.filter(
          (column) => !removedColumns.includes(column),
        );
        return {
          view: {
            ...view,
            columns: columns.length ? columns : this.getDefaultView().columns,
          },
          savable: true,
          removedColumns,
        };
      }),
    );
  }

  private hasField(
    column: ColumnWithTranslation,
    fieldKeys: (Set<string> | null)[],
  ): boolean {
    if (!column.field) {
      return false;
    }
    if (!ColumnViewService.singleFieldTemplate.test(column.field)) {
      return true;
    }

    const key = this.getFieldKey(column);
    const keys = fieldKeys[this.getRelatedEntityIndex(key)];
    return !keys || keys.has(key);
  }

  private getFieldKeys(
    entity: Octopus.EntityCode,
    compatibility?: Octopus.Compatibility,
  ): Observable<Set<string> | null> {
    const collect = (fields: Field[], keys: Set<string>): Set<string> => {
      fields.forEach((field) => {
        if (field.metadata?.key) {
          keys.add(field.metadata.key);
        }
        field.metadata?.alias?.forEach((alias) => keys.add(alias));
        collect(Object.values(field.fields ?? {}), keys);
      });
      return keys;
    };

    return this.metadataService.getEntity(entity, false, compatibility).pipe(
      map((catalog) => {
        const keys = collect(Object.values(catalog.fields), new Set<string>());
        return keys.size ? keys : null;
      }),
      catchError(() => of(null)),
    );
  }

  private fromColumnSetup(columnSetup: Octopus.ColumnSetup): ColumnView {
    return new Octopus.ColumnViewMapper(
      (template, header, width) => this.createColumn(template, header, width),
      this.getDefaultView().columns,
    ).from(columnSetup);
  }
}
